import { useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { WorkstreamType } from "@/types";
import { WORKSTREAM_TYPES } from "@/types";
import { Button, TextField } from "@/components/ui";
import { workstreamTypeLabel } from "@/lib/jobs";
import { FormDrawer } from "./FormDrawer";

/** Create a drawing set (Addendum 2 §3). Seeds one drawing per chosen type
 *  (planning/other also get a draw task; BC does not). The backend 400s with
 *  "Finish the current drawing set first" if the current set is still
 *  incomplete — the caller gates the button, and we surface the message here. */
export function NewDrawingSetDrawer({
  jobId,
  onClose,
  onSaved,
}: {
  jobId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState("");
  const [seedTypes, setSeedTypes] = useState<Set<WorkstreamType>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function toggle(type: WorkstreamType) {
    setSeedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  async function submit() {
    if (saving) return;
    if (seedTypes.size === 0) {
      setError("Choose at least one drawing type to seed.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await jobsApi.createDrawingSet(jobId, {
        label: label.trim() || undefined,
        seed_types: WORKSTREAM_TYPES.filter((t) => seedTypes.has(t)),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the drawing set.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="New drawing set"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Create drawing set</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <TextField
          label="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Rear extension — 2026"
        />

        <p className="field__label">Seed drawings</p>
        <div className="job-scope__checks">
          {WORKSTREAM_TYPES.map((t) => (
            <label key={t} className="job-scope__check">
              <input type="checkbox" checked={seedTypes.has(t)} onChange={() => toggle(t)} />
              {workstreamTypeLabel(t)}
            </label>
          ))}
        </div>
        <p className="job-drawer__muted">
          One drawing is created per type. Planning &amp; other also get a draw task; building control gets one via “Create task”.
        </p>
      </section>
    </FormDrawer>
  );
}
